import { Controller } from "@hotwired/stimulus"

// ネイティブ <select> を検索可能なコンボボックスに拡張する。
// 元の <select> はフォーム送信・required 検証の真実源として残し、画面外に隠す。
// その上に検索用テキスト入力と絞り込み済みの候補リストを描画する。
//
// 使い方:
//   <div data-controller="searchable-select">
//     <%= f.collection_select :candidate_id, Candidate.order(:name), :id, :name,
//           { prompt: "候補者を選択" }, { class: "form-input w-full", required: true } %>
//   </div>
export default class extends Controller {
  connect() {
    this.select = this.element.querySelector("select")
    if (!this.select) return

    // 元の <select> は残したまま視覚的に隠す。display:none ではなく画面外配置にすることで、
    // フォーム送信値の保持と required の検証（フォーカス可能であること）を維持する。
    Object.assign(this.select.style, {
      position: "absolute",
      width: "1px",
      height: "1px",
      padding: "0",
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "nowrap",
      border: "0"
    })
    this.select.setAttribute("tabindex", "-1")
    this.select.setAttribute("aria-hidden", "true")

    this.readOptions()

    this.placeholder = this.placeholderText()
    this.activeIndex = -1

    this.buildUi()
    this.syncInputFromSelect()

    this.onDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.onDocumentClick)

    // 外部（例: company-job-filter）が <select> の候補を絞り込んだら候補を作り直す。
    this.onRefresh = this.refreshOptions.bind(this)
    this.select.addEventListener("searchable-select:refresh", this.onRefresh)
  }

  disconnect() {
    // connect() が select 不在で早期リターンした場合、これらは未設定なのでガードする。
    if (this.onDocumentClick) document.removeEventListener("click", this.onDocumentClick)
    if (this.select && this.onRefresh) this.select.removeEventListener("searchable-select:refresh", this.onRefresh)
  }

  // <select> の option を { value, label } の配列へ取り込む。
  // prompt（value 空）と hidden/disabled（外部フィルタで除外された候補）は載せない。
  readOptions() {
    this.options = Array.from(this.select.options)
      .filter((option) => option.value !== "" && !option.hidden && !option.disabled)
      .map((option) => ({ value: option.value, label: option.text }))
  }

  // 外部フィルタ後に候補と表示を作り直す。開いていれば現在の入力で再描画する。
  refreshOptions() {
    this.readOptions()
    this.syncInputFromSelect()
    if (!this.list.classList.contains("hidden")) this.filter(this.input.value)
  }

  // prompt（先頭の空 value）の表示文字列をプレースホルダに流用する。
  placeholderText() {
    const prompt = Array.from(this.select.options).find((option) => option.value === "")
    return prompt ? prompt.text : "検索して選択"
  }

  buildUi() {
    this.wrapper = document.createElement("div")
    this.wrapper.className = "relative"

    this.input = document.createElement("input")
    this.input.type = "text"
    this.input.className = this.select.getAttribute("class") || "form-input w-full"
    this.input.placeholder = this.placeholder
    this.input.autocomplete = "off"
    this.input.setAttribute("role", "combobox")
    this.input.setAttribute("aria-expanded", "false")

    this.list = document.createElement("ul")
    this.list.className =
      "absolute z-20 mt-1 max-h-60 w-full list-none overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg hidden"
    this.list.setAttribute("role", "listbox")

    this.wrapper.appendChild(this.input)
    this.wrapper.appendChild(this.list)
    this.element.appendChild(this.wrapper)

    this.input.addEventListener("focus", () => this.open())
    this.input.addEventListener("input", () => this.filter(this.input.value))
    this.input.addEventListener("keydown", (event) => this.onKeydown(event))
    this.input.addEventListener("click", () => this.open())
    // フォーカスがコントローラの外へ移ったときのみ閉じる。
    // input の blur で閉じるとスクロールバーやリスト余白のクリックでも閉じてスクロール操作を妨げるため focusout を使う。
    this.element.addEventListener("focusout", (event) => {
      if (event.relatedTarget && !this.element.contains(event.relatedTarget)) this.close()
    })
  }

  // 現在の <select> の選択値を検索入力の表示へ反映する。
  syncInputFromSelect() {
    const selected = this.options.find((option) => option.value === this.select.value)
    this.input.value = selected ? selected.label : ""
  }

  // フォーカス/クリックでドロップダウンを開く。既に開いていれば何もしない。
  // 全候補を表示し、入力テキストを全選択して即タイプで絞り込めるようにする。
  open() {
    if (!this.list.classList.contains("hidden")) return
    // 開いた直後は選択中の項目をハイライトする（Enter で先頭候補へ誤って切り替わらないようにする）。
    this.showOptions(this.options, { highlightSelected: true })
    this.input.select()
  }

  close() {
    this.list.classList.add("hidden")
    this.input.setAttribute("aria-expanded", "false")
    this.activeIndex = -1
    // 確定済みの選択と入力テキストがずれていたら表示を選択値へ戻す（未確定の打ち込みを残さない）。
    this.syncInputFromSelect()
  }

  // クエリで候補を絞り込んでリストを再描画する。空なら全件表示。
  // 絞り込み中は先頭候補をハイライトして Enter で即確定できるようにする。
  filter(query) {
    const needle = query.trim().toLowerCase()
    const matched =
      needle === "" ? this.options : this.options.filter((option) => option.label.toLowerCase().includes(needle))
    this.showOptions(matched, { highlightSelected: false })
  }

  // 与えた候補配列でリストを開いて描画する。
  // highlightSelected: true なら選択中の項目を、false なら先頭をハイライトする。
  showOptions(matched, { highlightSelected = false } = {}) {
    this.visibleOptions = matched
    if (highlightSelected) {
      const selectedIndex = matched.findIndex((option) => option.value === this.select.value)
      this.activeIndex = selectedIndex >= 0 ? selectedIndex : (matched.length > 0 ? 0 : -1)
    } else {
      this.activeIndex = matched.length > 0 ? 0 : -1
    }
    this.renderList()
    this.list.classList.remove("hidden")
    this.input.setAttribute("aria-expanded", "true")
    if (this.activeIndex >= 0) this.highlight()
  }

  renderList() {
    this.list.innerHTML = ""
    if (this.visibleOptions.length === 0) {
      const empty = document.createElement("li")
      empty.className = "px-3 py-2 text-gray-400"
      empty.textContent = "該当なし"
      this.list.appendChild(empty)
      return
    }

    this.visibleOptions.forEach((option, index) => {
      const item = document.createElement("li")
      item.className = this.itemClass(index === this.activeIndex)
      item.textContent = option.label
      item.setAttribute("role", "option")
      // mousedown で確定（input の blur より先に処理してリストが閉じないようにする）。
      item.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.choose(option)
      })
      // mouseenter は要素に乗った瞬間に1回だけ発火するため、mousemove のように
      // マウス移動のたび highlight() が連続実行されることがなく無駄な再描画を抑えられる。
      item.addEventListener("mouseenter", () => {
        this.activeIndex = index
        this.highlight()
      })
      this.list.appendChild(item)
    })
  }

  itemClass(active) {
    return `cursor-pointer px-3 py-2 ${active ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`
  }

  highlight() {
    // 候補が無いとき（「該当なし」表示のみ）はクラスを上書きしない。
    if (!this.visibleOptions || this.visibleOptions.length === 0) return
    Array.from(this.list.children).forEach((item, index) => {
      item.className = this.itemClass(index === this.activeIndex)
    })
    const active = this.list.children[this.activeIndex]
    if (active) active.scrollIntoView({ block: "nearest" })
  }

  // 候補を選択して <select> へ反映し、change を発火する（依存する挙動と互換）。
  choose(option) {
    this.select.value = option.value
    this.select.dispatchEvent(new Event("change", { bubbles: true }))
    this.input.value = option.label
    this.list.classList.add("hidden")
    this.input.setAttribute("aria-expanded", "false")
    // 確定後もフォーカスが残るため、確定ラベルを全選択しておく。
    // こうすると次の入力（IME 含む）が追記でなく置換になり、「田中 太郎田中」のような連結を防ぐ。
    this.input.select()
  }

  onKeydown(event) {
    // IME変換中（日本語入力など）の Enter・矢印は変換確定/候補移動のためのものなので、
    // コントローラ側では処理せず IME に委ねる。これをしないと「村」を確定する Enter で
    // 候補確定＋全選択が走り、続けて打った「岡」が選択範囲を置換して「村」が消える。
    if (event.isComposing || event.keyCode === 229) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Enter":
        if (!this.list.classList.contains("hidden") && this.activeIndex >= 0) {
          event.preventDefault()
          this.choose(this.visibleOptions[this.activeIndex])
        }
        break
      case "Escape":
        this.close()
        break
    }
  }

  move(step) {
    if (!this.visibleOptions || this.visibleOptions.length === 0) return
    // 閉じていれば開くだけ（選択中の項目がハイライトされる）。最初の矢印では移動しない。
    if (this.list.classList.contains("hidden")) {
      this.open()
      return
    }
    const count = this.visibleOptions.length
    // 未ハイライト(-1)からは ArrowDown で先頭、ArrowUp で末尾へ（末尾スキップを防ぐ）。
    this.activeIndex =
      this.activeIndex === -1 ? (step > 0 ? 0 : count - 1) : (this.activeIndex + step + count) % count
    this.highlight()
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }
}
